"use client";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogCloseButton } from "@shared/src/components/ui/dialog";
import { cn } from "@shared/src/utils/cn";
import { useState, useMemo } from "react";
import { PaymentMethodModal } from "@shared/src/components/common/dialogs/PaymentMethodModal";
import { ChevronDown, PlusIcon } from "lucide-react";
import { Skeleton } from "@shared/src/components/ui/skeleton";
import { SearchInput } from "@shared/src/components/common/inputs/SearchInput";
import { Button } from "@shared/src/components/ui/button";
import { useUserPaymentConfigs } from "@shared/src/hooks/useUserPaymentConfigs";
import { PaymentMethod, AccountPaymentConfig } from "@sdk/shared/types/config.types";
import { TextWithIndicator } from "@shared/src/components/common/misc/TextWithIndicator";
import { generateColorFromString } from "@shared/src/utils";
import { useAuthStore } from "@shared/src/stores/useAuthStore";

interface PaymentMethodSelectProps {
  label: string;
  error?: boolean;
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  tradeType: "buy" | "sell";
  adPaymentMethods: PaymentMethod[];
  containerClassName?: string;
  disabled?: boolean;
}

interface SelectedItemType {
  label: string;
  account?: string;
}

interface PaymentMethodItemProps {
  method: PaymentMethod;
  config?: AccountPaymentConfig;
  selected: boolean;
  onSelect: () => void;
}

const PaymentMethodItem = ({ method, config, selected, onSelect }: PaymentMethodItemProps) => (
  <div
    onClick={onSelect}
    className={cn(
      "flex flex-col gap-2 p-4 rounded-2xl border cursor-pointer transition-all",
      selected
        ? "bg-surfaces-bg-secondary border-border-focus"
        : "bg-surfaces-bg-secondary-darker border-transparent hover:border-border-tertiary",
    )}
  >
    <div className="flex items-center justify-between">
      <TextWithIndicator
        text={method.name}
        indicatorClassName="h-4 w-0.5"
        className="text-text-primary font-medium"
        indicatorColor={`#${method.color}` || generateColorFromString(method.id)}
      />
    </div>
    {config && (
      <div className="ml-2">
        <p className="text-small text-text-secondary max-sm:text-small">{config.accountName}</p>
        <p className="text-small text-text-tertiary max-sm:text-small">{config.account}</p>
      </div>
    )}
  </div>
);

const ConfigSkeleton = () => (
  <div className="flex flex-col gap-2 p-4 rounded-2xl bg-surfaces-bg-secondary-darker">
    <div className="flex items-center gap-2">
      <Skeleton className="h-4 w-0.5" />
      <Skeleton className="h-4 w-32" />
    </div>
    <div className="ml-2 space-y-2">
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-3 w-32" />
    </div>
  </div>
);

export function PaymentMethodSelect({
  label,
  error,
  value,
  onValueChange,
  placeholder = "Select my payment method",
  tradeType,
  adPaymentMethods,
  containerClassName,
  disabled = false,
}: PaymentMethodSelectProps) {
  const { activeAccount } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMethodForAdd, setSelectedMethodForAdd] = useState<PaymentMethod | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  // Add authentication check to disabled state
  const isDisabled = disabled || !activeAccount?.isAuthenticated;

  const { userPaymentConfigs: paymentConfigs, refetch, isLoading } = useUserPaymentConfigs(tradeType === "sell");

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (tradeType === "buy") {
      if (!searchQuery) return adPaymentMethods;
      return adPaymentMethods.filter(method => method.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    return adPaymentMethods;
  }, [tradeType, adPaymentMethods, searchQuery]);

  // Filter configs based on search query
  const filteredConfigs = useMemo(() => {
    if (!searchQuery || tradeType === "buy") return paymentConfigs;
    return paymentConfigs.filter(
      config =>
        config.accountName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        adPaymentMethods
          .find(m => m.id === config.methodId)
          ?.name.toLowerCase()
          .includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery, paymentConfigs, adPaymentMethods, tradeType]);

  // Get selected item details
  const selectedItem = useMemo((): SelectedItemType | null => {
    if (!value) return null;

    if (tradeType === "buy") {
      const method = adPaymentMethods.find(method => String(method.id) === value);
      return method
        ? {
            label: method.name,
          }
        : null;
    }

    const config = paymentConfigs.find(config => String(config.id) === value);
    if (!config) return null;

    const method = adPaymentMethods.find(m => m.id === config.methodId);
    return {
      label: method?.name || "",
      account: config.account,
    };
  }, [value, tradeType, adPaymentMethods, paymentConfigs]);

  const handleAddNewConfig = (method: PaymentMethod) => {
    setSelectedMethodForAdd(method);
    setIsModalOpen(true);
    setIsOpen(false);
  };

  const handleModalSuccess = (config: AccountPaymentConfig) => {
    onValueChange(String(config.id));
    setSelectedMethodForAdd(null);
    setIsModalOpen(false);
    refetch();
  };

  return (
    <>
      <div className={cn("space-y-1.5", containerClassName)}>
        <div
          className={cn(
            "relative w-full rounded-2xl border border-border-tertiary bg-surfaces-bg-primary transition-all duration-200 h-[62px]",
            error ? "border-border-error" : isFocused && "border-border-focus",
            isDisabled && "opacity-50 cursor-not-allowed",
          )}
        >
          <label
            className={cn(
              "absolute -top-2 left-3 !text-small bg-surfaces-bg-primary px-2 transition-all duration-200",
              error ? "text-text-error" : isFocused ? "text-text-brand" : "text-text-tertiary",
            )}
          >
            {label}
          </label>

          <button
            type="button"
            disabled={isDisabled}
            onClick={() => !isDisabled && setIsOpen(true)}
            className="w-full h-full flex items-center justify-between px-4 text-left"
          >
            {selectedItem ? (
              <div className="flex items-center justify-between w-full">
                <p className="text-small-2 text-text-primary">{selectedItem.label}</p>
                {selectedItem.account && <p className="text-small-2 text-text-primary">{selectedItem.account}</p>}
              </div>
            ) : (
              <span className="text-text-tertiary max-sm:text-small">{placeholder}</span>
            )}
            <ChevronDown
              className={cn("h-5 w-5 text-text-tertiary transition-transform ml-2", isOpen && "transform rotate-180")}
            />
          </button>
        </div>
        {isDisabled && !disabled && <p className="text-small text-text-tertiary ml-4">Login to make a trade</p>}
      </div>

      {/* Only render Dialog if user is authenticated */}
      {activeAccount?.isAuthenticated && (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent
            size="lg"
            background="primary"
            padding="md"
            position="center-mobile-bottom"
            hideCloseButton={true}
            height="85dvh"
            layout="flex-col-overflow"
            className="gap-0"
          >
            <DialogHeader className="flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <DialogTitle className="text-h6 font-extrabold text-text-primary">{label}</DialogTitle>
                <DialogCloseButton onClose={() => setIsOpen(false)} />
              </div>
            </DialogHeader>

            <div className="sticky top-0 py-4 bg-surfaces-bg-primary">
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                onClear={() => setSearchQuery("")}
                placeholder="Search payment methods..."
              />
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {tradeType === "buy" ? (
                <div className="grid grid-cols-1 gap-2">
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map(method => (
                      <PaymentMethodItem
                        key={method.id}
                        method={method}
                        selected={value === String(method.id)}
                        onSelect={() => {
                          onValueChange(String(method.id));
                          setIsOpen(false);
                        }}
                      />
                    ))
                  ) : (
                    <div className="px-4 py-2 text-text-tertiary text-center">No Payment Methods found</div>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Existing Payment Methods */}
                  <div className="space-y-2">
                    <p className="text-small font-bold text-text-tertiary max-sm:text-small">Your payment methods</p>
                    <div className="grid grid-cols-1 gap-2">
                      {isLoading ? (
                        <>
                          <ConfigSkeleton />
                          <ConfigSkeleton />
                        </>
                      ) : filteredConfigs.length > 0 ? (
                        filteredConfigs.map(config => {
                          const method = adPaymentMethods.find(m => m.id === config.methodId);
                          if (!method) return null;
                          return (
                            <PaymentMethodItem
                              key={config.id}
                              method={method}
                              config={config}
                              selected={value === String(config.id)}
                              onSelect={() => {
                                onValueChange(String(config.id));
                                setIsOpen(false);
                              }}
                            />
                          );
                        })
                      ) : (
                        <div className="px-4 py-2 text-text-tertiary text-center">No payment methods found</div>
                      )}
                    </div>
                  </div>

                  {/* Add New Payment Methods */}
                  <div className="space-y-2">
                    <p className="text-small font-bold text-text-tertiary max-sm:text-small">
                      Add supported payment methods
                    </p>
                    <div className="grid grid-cols-1 gap-2">
                      {filteredOptions.map(method => (
                        <button
                          key={method.id}
                          type="button"
                          onClick={() => handleAddNewConfig(method)}
                          className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl border-2 border-dashed border-border-tertiary hover:border-border-focus transition-colors text-text-tertiary hover:text-text-primary max-sm:text-small"
                        >
                          <PlusIcon size={16} />
                          Add new {method.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Only render PaymentMethodModal if user is authenticated */}
      {activeAccount?.isAuthenticated && (
        <PaymentMethodModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          preSelectedMethod={selectedMethodForAdd}
          disableMethodSelection={true}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
}
